import { createReadStream } from 'node:fs';
import { createInterface } from 'node:readline';
import { join } from 'node:path';
import type { ServerLog, ServerLogResponse } from '../types';
import { fileExists } from '../utils';
import { parsePinoLog, parseStdLog } from './parsers';

/**
 * Read ServerLog logs from multiple sources
 *
 * Reads from 4 log files:
 * - server.log (Pino JSON)
 * - trace.log (Pino JSON)
 * - server.std.log (plain text)
 * - client.std.log (plain text)
 */
export async function readServerLogs(
  logDir: string,
  options: {
    limit?: number;
    offset?: number;
    levels?: string[];
    sources?: string[];
  } = {}
): Promise<ServerLogResponse | undefined> {
  const limit = options.limit || 100;
  const offset = options.offset || 0;
  const sources = options.sources || ['server', 'trace', 'server-std', 'client-std'];

  const allLogs: ServerLog[] = [];
  const errors: string[] = [];

  for (const source of sources) {
    try {
      const logs = await readLogsBySource(logDir, source);
      allLogs.push(...logs);
    } catch (error) {
      const errorMsg = `Failed to read ${source}: ${error instanceof Error ? error.message : String(error)}`;
      errors.push(errorMsg);
      console.warn(`[readServerLogs] ${errorMsg}`);
    }
  }

  if (allLogs.length === 0) {
    if (errors.length > 0) {
      console.warn(`[readServerLogs] No logs found. Errors: ${errors.join(', ')}`);
    }
    return undefined;
  }

  let filteredLogs = allLogs;
  if (options.levels && options.levels.length > 0) {
    filteredLogs = allLogs.filter(log => options.levels!.includes(log.level));
  }

  // Sort by timestamp descending (newest first)
  filteredLogs.sort((a, b) => b.timestamp - a.timestamp);

  const total = filteredLogs.length;
  const paginatedLogs = filteredLogs.slice(offset, offset + limit);

  return {
    logs: paginatedLogs,
    total,
    hasMore: offset + limit < total,
  };
}

/**
 * Read logs from a specific source
 */
async function readLogsBySource(
  logDir: string,
  source: string
): Promise<ServerLog[]> {
  let filePath: string;
  let parser: (line: string) => ServerLog | null;

  if (source === 'server') {
    filePath = join(logDir, 'server.log');
    parser = (line) => parsePinoLog(line, 'server');
  } else if (source === 'trace') {
    filePath = join(logDir, 'trace.log');
    parser = (line) => parsePinoLog(line, 'trace');
  } else if (source === 'server-std') {
    filePath = join(logDir, 'server.std.log');
    parser = (line) => parseStdLog(line, 'server-std');
  } else if (source === 'client-std') {
    filePath = join(logDir, 'client.std.log');
    parser = (line) => parseStdLog(line, 'client-std');
  } else {
    console.warn(`[readLogsBySource] Unknown source: ${source}`);
    return [];
  }

  if (!(await fileExists(filePath))) {
    console.warn(`[readLogsBySource] File not found: ${filePath}`);
    return [];
  }

  const logs: ServerLog[] = [];
  let stream: ReturnType<typeof createReadStream> | null = null;
  let rl: ReturnType<typeof createInterface> | null = null;

  try {
    stream = createReadStream(filePath, { encoding: 'utf8' });
    rl = createInterface({ input: stream, crlfDelay: Infinity });

    for await (const line of rl) {
      if (!line.trim()) continue;

      try {
        const log = parser(line);
        if (log) {
          logs.push(log);
        }
      } catch (parseError) {
        // Ignore individual line parse errors
      }
    }
  } catch (error) {
    console.error(`[readLogsBySource] Error reading ${filePath}:`, error);
    throw error;
  } finally {
    if (rl) {
      rl.close();
    }
    if (stream) {
      stream.close();
    }
  }

  return logs;
}
